import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card, Spinner, Alert, Badge, Button, Row, Col } from 'react-bootstrap';
import { fetchSubOrgDetails } from '../../../actioncreators/actioncreators';
import './SubOrgManager.css';

// Small presentational helper for a label/value pair.
const InfoRow = ({ label, value }) => (
  <div className="sod-info-row">
    <span className="sod-info-label">{label}</span>
    <span className="sod-info-value">{value || '—'}</span>
  </div>
);

const StatusBadge = ({ status }) => (
  <Badge bg={status === 'ACTIVE' ? 'success' : 'secondary'}>{status || 'N/A'}</Badge>
);

/**
 * Sub-Organization View screen.
 * Shows the four sections required for a sub-org:
 *   1. Total number of members
 *   2. Organization hierarchy (breadcrumb of ancestors + direct children)
 *   3. Admin (manager) contact details
 *   4. Organization information (the sub-org itself + its parent organization)
 */
const SubOrgDetails = () => {
  const { subOrgId } = useParams();
  const navigate = useNavigate();
  // Needed so a Super Admin viewing another org's sub-org queries the right org.
  const superAdminSelectedOrgId = useSelector((state) => state.superAdminSelectedOrgId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!subOrgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetchSubOrgDetails(subOrgId, superAdminSelectedOrgId);
        if (cancelled) return;
        const payload = res.data?.data || null;
        if (!payload) {
          setError('Sub-organization not found.');
        } else {
          setData(payload);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'Failed to load sub-organization details.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subOrgId, superAdminSelectedOrgId]);

  if (loading) {
    return (
      <div className="sub-org-manager-page text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sub-org-manager-page">
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate('/home/sub-organizations')}>
          Back to Sub-Organizations
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const ancestors = data.ancestors || [];
  const children = data.children || [];

  return (
    <div className="sub-org-manager-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div>
            <h1 className="page-title">{data.sub_org_name}</h1>
            <p className="page-subtitle">
              {data.sub_org_code ? `Code: ${data.sub_org_code}` : 'Sub-Organization details'}
            </p>
          </div>
        </div>
        <Button variant="outline-secondary" onClick={() => navigate('/home/sub-organizations')}>
          Back
        </Button>
      </div>

      {/* 1. Key counts — members first, as required */}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="sod-stat-card">
            <Card.Body>
              <div className="sod-stat-value">{data.user_count ?? 0}</div>
              <div className="sod-stat-label">Total Members</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="sod-stat-card">
            <Card.Body>
              <div className="sod-stat-value">{data.location_count ?? 0}</div>
              <div className="sod-stat-label">Locations</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="sod-stat-card">
            <Card.Body>
              <div className="sod-stat-value">{data.child_sub_org_count ?? 0}</div>
              <div className="sod-stat-label">Child Sub-Organizations</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        {/* 2. Organization hierarchy */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header><strong>Organization Hierarchy</strong></Card.Header>
            <Card.Body>
              <div className="sod-breadcrumb">
                {data.org_name && (
                  <>
                    <span className="sod-crumb sod-crumb-org">{data.org_name}</span>
                    <span className="sod-crumb-sep">›</span>
                  </>
                )}
                {ancestors.map((a) => (
                  <React.Fragment key={a.sub_org_id}>
                    <span className="sod-crumb">{a.sub_org_name}</span>
                    <span className="sod-crumb-sep">›</span>
                  </React.Fragment>
                ))}
                <span className="sod-crumb sod-crumb-current">{data.sub_org_name}</span>
              </div>

              <div className="mt-3">
                <div className="sod-section-label">
                  Direct Child Sub-Organizations ({children.length})
                </div>
                {children.length === 0 ? (
                  <p className="text-muted mb-0">No child sub-organizations.</p>
                ) : (
                  <ul className="sod-child-list">
                    {children.map((ch) => (
                      <li key={ch.sub_org_id}>
                        <span
                          className="sod-child-link"
                          onClick={() => navigate(`/home/sub-organizations/${ch.sub_org_id}`)}
                        >
                          {ch.sub_org_name}
                        </span>
                        <span className="text-muted"> — {ch.user_count ?? 0} member(s)</span>
                        <StatusBadge status={ch.status} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* 3. Admin contact details */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header><strong>Admin Contact Details</strong></Card.Header>
            <Card.Body>
              {data.manager_name ? (
                <>
                  <InfoRow label="Name" value={data.manager_name} />
                  <InfoRow label="Role" value={data.manager_role} />
                  <InfoRow label="Employee ID" value={data.manager_employee_id} />
                  <InfoRow label="Email" value={data.manager_email} />
                  <InfoRow label="Mobile" value={data.manager_mobile} />
                </>
              ) : (
                <p className="text-muted mb-0">No manager assigned to this sub-organization.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* 4. Organization information */}
        <Col md={12}>
          <Card>
            <Card.Header><strong>Organization Information</strong></Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div className="sod-section-label">Sub-Organization</div>
                  <InfoRow label="Name" value={data.sub_org_name} />
                  <InfoRow label="Code" value={data.sub_org_code} />
                  <InfoRow label="Description" value={data.description} />
                  <InfoRow label="Parent Sub-Org" value={data.parent_sub_org_name} />
                  <div className="sod-info-row">
                    <span className="sod-info-label">Status</span>
                    <span className="sod-info-value"><StatusBadge status={data.status} /></span>
                  </div>
                  <InfoRow
                    label="Created"
                    value={data.creation_date_and_time
                      ? new Date(data.creation_date_and_time).toLocaleString()
                      : ''}
                  />
                </Col>
                <Col md={6}>
                  <div className="sod-section-label">Parent Organization</div>
                  <InfoRow label="Name" value={data.org_name} />
                  <InfoRow label="Email" value={data.org_email} />
                  <InfoRow label="Mobile" value={data.org_mobile} />
                  <InfoRow label="City" value={data.org_city} />
                  <InfoRow label="State" value={data.org_state} />
                  <div className="sod-info-row">
                    <span className="sod-info-label">Status</span>
                    <span className="sod-info-value"><StatusBadge status={data.org_status} /></span>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SubOrgDetails;
